/* eslint-disable react-hooks/exhaustive-deps */
import {
  Typography,
  Grid,
  Card,
  Stack,
  FormControlLabel,
  Checkbox,
  Box,
  Pagination,
  Divider,
  Drawer,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CardConfig from 'components/CardConfig';
import { useFormik } from 'formik';
import debounce from 'lodash/debounce';
import React, { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import toast from 'react-hot-toast';
import collegeServices, { MyCollegesRecord } from 'services/collegeServices';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import LoadingContent from 'components/LoadingContent';
import { InlineSpinner } from './usePendingAction';
import { useHistory } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { checkEnableParseJSON } from 'components/helper/helper';
import ModalMajor from './ModalMajor';
import ModalLocation from './ModalLocation';
import ModalAdmissionRequirement from './ModalAdmissionRequirement';
import { ModalTypeSize, ModalCost, ModalOutcomes } from './FilterGroupModals';
import counselorServices from 'services/counselorServices';
import Container from 'react-bootstrap/Container';
import { getAccessToken, getUserId, removeUserId, removeAccessToken } from 'utils/sessionStorage';
import { STRIPE_PRICE_NAME } from 'constants/common';
import 'styles/college-management.css';

// SPEC_College_Search_Filters (v1.1) — sort options. `value` maps to the
// server-side ORDER BY whitelist; empty string = default (A-Z by name).
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Name (A–Z)' },
  { value: 'acceptance_rate_desc', label: 'Acceptance rate (high → low)' },
  { value: 'acceptance_rate_asc', label: 'Acceptance rate (low → high)' },
  { value: 'median_earnings_desc', label: 'Median earnings (high → low)' },
  { value: 'tuition_asc', label: 'Tuition (low → high)' },
  { value: 'graduation_rate_desc', label: 'Graduation rate (high → low)' },
  { value: 'ranking_asc', label: 'US News ranking (best first)' },
];

// ── Icons (inline SVG helpers to avoid new icon-package deps) ──────────────
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconX = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconMapPin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconBookmark = ({ filled = false }: { filled?: boolean }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconLink = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);
const IconDollar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
  </svg>
);
// ── Filter-group icons (Lovable taxonomy) ────────────────────────────────────
const fIcon = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconLocation = () => (
  <svg {...fIcon}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
);
const IconMajors = () => (
  <svg {...fIcon}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
);
const IconTypeSize = () => (
  <svg {...fIcon}><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg>
);
const IconAdmission = () => (
  <svg {...fIcon}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
);
const IconCost = () => (
  <svg {...fIcon}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
);
const IconOutcomes = () => (
  <svg {...fIcon}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
);

// ── Brand tokens ─────────────────────────────────────────────────────────────
const BRAND_NAVY   = '#2E5266';
const BRAND_ORANGE = '#F4A261';

// ── Types ─────────────────────────────────────────────────────────────────────
export const typoSx = {
  title: { fontWeight: 700, fontSize: 20, marginBottom: 2 },
};

export type TCollege = {
  w_gpa?: boolean;
  url_price_calc?: string;
  counselor_ranking?: string;
  student_ranking?: string;
  act: string;
  address_line_1: string;
  address_line_2: string;
  area_code_1: string;
  area_code_2: string;
  area_code_fax: string;
  city: string;
  cmps_setting: string;
  college_id: number;
  school_id?: number | null;
  country_code: string;
  county: string;
  difficulty_for_admittance: string;
  founding_year: number;
  hous_frsh_policy: string;
  id: number;
  life_frat_nat: string;
  life_sor_nat: string;
  name: string;
  phone_number_1: string;
  phone_number_2: string;
  phone_number_fax: string;
  region: string;
  size_of_school: string;
  state: string;
  state_name: string;
  under_student: number;
  url_address: string;
  year_type: string;
  zip_code: string;
  is_saved: boolean;
  gpa?: number;
  sat?: number | string;
  type_of_school?: string;
  // SPEC_College_Search_Filters (v1.1) — Tier 1 + Tier 2 display fields
  application_type?: string;
  testing_policy?: string;
  acceptance_rate?: number | null;
  graduation_rate?: number | null;
  graduation_rate_4yr?: number | null;
  retention_rate?: number | null;
  in_state_tuition?: number | null;
  out_of_state_tuition?: number | null;
  room_board?: number | null;
  average_net_price?: number | null;
  offers_merit_aid?: boolean;
  meets_full_need?: boolean;
  has_loan_free_policy?: boolean;
  has_free_tuition_policy?: boolean;
  median_earnings_10yr?: number | null;
  employment_rate?: number | null;
  loan_default_rate?: number | null;
  hbcu?: boolean;
  hispanic_serving_institution?: boolean;
  religious_affiliation?: string;
  carnegie_classification?: string;
  campus_size_acres?: number | null;
  gender_type?: string;
};

export const MY_COLLEGES_LIST_NAME = 'My Colleges';

/**
 * Preferred single key for maps (Admission / My Take). Prefer CAM `school_id`,
 * then GradMap `college_id`, then legacy `id`.
 */
export function getCollegeKey(college: any): string | null {
  const schoolId = Number(college?.school_id);
  if (Number.isFinite(schoolId) && schoolId > 0) return `school:${schoolId}`;
  const collegeId = Number(college?.college_id);
  if (Number.isFinite(collegeId) && collegeId > 0) return `college:${collegeId}`;
  const rawId = Number(college?.id);
  if (Number.isFinite(rawId) && rawId > 0) return `id:${rawId}`;
  const name = typeof college?.name === 'string' ? college.name.trim().toLowerCase() : '';
  if (!name) return null;
  const state = typeof college?.state === 'string' ? college.state.trim().toLowerCase() : '';
  return `name:${name}|${state}`;
}

/** All known identities for a college — used to match Search ↔ My Colleges across CAM sync. */
function getCollegeIdentityKeys(college: any): string[] {
  const keys = new Set<string>();
  const schoolId = Number(college?.school_id);
  if (Number.isFinite(schoolId) && schoolId > 0) keys.add(`school:${schoolId}`);
  const collegeId = Number(college?.college_id);
  if (Number.isFinite(collegeId) && collegeId > 0) keys.add(`college:${collegeId}`);
  const rawId = Number(college?.id);
  if (Number.isFinite(rawId) && rawId > 0) {
    keys.add(`id:${rawId}`);
    // Search results often use `id` as the GradMap college_id.
    keys.add(`college:${rawId}`);
  }
  const name = typeof college?.name === 'string' ? college.name.trim().toLowerCase() : '';
  if (name) {
    const state = typeof college?.state === 'string' ? college.state.trim().toLowerCase() : '';
    keys.add(`name:${name}|${state}`);
  }
  return [...keys];
}

export function isSameCollege(a: any, b: any): boolean {
  const ka = getCollegeIdentityKeys(a);
  const kb = getCollegeIdentityKeys(b);
  if (ka.length === 0 || kb.length === 0) return false;
  return ka.some((key) => kb.includes(key));
}

export type CollegeListGroup = {
  id?: number;
  name: string;
  collegeList: TCollege[];
  created_by?: 'student' | 'counselor';
  counselor_name?: string;
  description?: string;
};

/** API may return null for optional strings; state type uses undefined only. */
export function mapApiCollegeListGroup(l: {
  id: number;
  name: string;
  description?: string | null;
  created_by?: 'student' | 'counselor';
  counselor_name?: string | null;
  collegeList?: Record<string, unknown>[] | TCollege[];
}): CollegeListGroup {
  return {
    id: l.id,
    name: l.name,
    collegeList: (l.collegeList ?? []) as TCollege[],
    created_by: l.created_by ?? 'student',
    description: l.description ?? undefined,
    counselor_name: l.counselor_name ?? undefined,
  };
}

export type ListCollegeByGroup = CollegeListGroup[];

// ── Plan limits ───────────────────────────────────────────────────────────────
export const PLAN_LIST_LIMITS: Record<string, number> = {
  [STRIPE_PRICE_NAME.GradMapStarter]: 1,
  [STRIPE_PRICE_NAME.SilverMonth]: 3,
  [STRIPE_PRICE_NAME.SilverYear]: 3,
  [STRIPE_PRICE_NAME.SilverPlusMonth]: 3,
  [STRIPE_PRICE_NAME.SilverPlusYear]: 3,
  [STRIPE_PRICE_NAME.GoldMonth]: 5,
  [STRIPE_PRICE_NAME.GoldYear]: 5,
};

// My Schools cap per plan (Plan_Tier_Feature_Matrix §2.2 / §3). Only Starter is
// capped; every paid plan is unlimited. Mirror of `plan_limits.*.my_schools_max`.
export const PLAN_SCHOOL_LIMITS: Record<string, number> = {
  [STRIPE_PRICE_NAME.GradMapStarter]: 10,
};

/** Max schools a student may keep in My Colleges; Infinity = unlimited (paid tiers). */
export function getMySchoolsMax(planName: string): number {
  return PLAN_SCHOOL_LIMITS[planName] ?? Infinity;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map((w) => w[0]).filter(Boolean).join('').toUpperCase();

export const type_of_school_convert = (str: string | undefined): string => {
  const data: Record<string, string> = {
    G: 'Public', GCI: 'Public', GCO: 'Public', GCW: 'Public',
    GDI: 'Public', GF: 'Public', GP: 'Public', GS: 'Public',
    GSL: 'Public', GSR: 'Public', GT: 'Public',
    P: 'Private', PN: 'Private', PP: 'Private', PR: 'Private',
  };
  return str?.toUpperCase() ? (data[str.toUpperCase()] ?? '') : '';
};

export const cmpsSettingConvert = (str: string | undefined): string => {
  const data: Record<string, string> = {
    RURAL: 'Rural', SMALL: 'Small Town', SUBRB: 'Suburban', URBAN: 'Urban',
  };
  return str?.toUpperCase() ? (data[str.toUpperCase()] ?? '') : '';
};

// ── Main Component ────────────────────────────────────────────────────────────
const CollegeSearch = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const history = useHistory();
  let studentId = getUserId();
  let token = getAccessToken();

  const stripeSubscriptionInfo = useAppSelector(
    (store: any) => store.basicInformation.stripeSubscriptionInfo
  );
  const planName: string = stripeSubscriptionInfo?.subcription_plan ?? STRIPE_PRICE_NAME.GradMapStarter;
  const isStarter = planName === STRIPE_PRICE_NAME.GradMapStarter;
  const maxLists: number = PLAN_LIST_LIMITS[planName] ?? 5;
  const mySchoolsMax: number = getMySchoolsMax(planName);

  const [openMajorSearch, setOpenMajorSearch] = React.useState(false);
  const [openLocationSearch, setOpenLocationSearch] = React.useState(false);
  const [openTypeSize, setOpenTypeSize] = React.useState(false);
  const [openAdmissionRequirement, setOpenAdmissionRequirement] = React.useState(false);
  const [openCost, setOpenCost] = React.useState(false);
  const [openOutcomes, setOpenOutcomes] = React.useState(false);
  const [openMobileFilters, setOpenMobileFilters] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [totalPage, setTotalPage] = React.useState(40);
  const [totalResults, setTotalResults] = React.useState(0);
  const [myCurrentList, setMyCurrentList] = React.useState<ListCollegeByGroup>([]);
  const [savedStudentLists, setSavedStudentLists] = React.useState<CollegeListGroup[]>([]);
  const [data, setData] = React.useState<any>(null);
  const [colleges, setColleges] = React.useState<TCollege[]>([]);
  const [openModal, setOpenModal] = React.useState<TCollege | undefined | null>();
  // Start in loading so the first paint shows the spinner, not the empty state,
  // while the filter-restore effect resolves persisted filters + first search.
  const [loading, setLoading] = React.useState(true);
  const [loadingLists, setLoadingLists] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [keyword, setKeyword] = React.useState('');
  // SPEC_College_Search_Filters (v1.1) — Lovable "Hide Reviewed" toggle hides
  // schools already saved in My Colleges (client-side, current page only).
  const [hideReviewed, setHideReviewed] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      keyword: '',
      state_name: '',
      major_names: [] as string[],
      state_codes: [] as any[],
      cmps_setting: [] as any[],
      size_of_school: [] as any[],
      life_frat_nat: '',
      life_sor_nat: '',
      from_gpa: null as any,
      to_gpa: null as any,
      from_sat: null as any,
      to_sat: null as any,
      from_act: null as any,
      to_act: null as any,
      region: null as any,
      type_of_school: [] as any[],
      year_types: [] as any[],
      // SPEC_College_Search_Filters (v1.1) — Tier 1 + Tier 2 "More filters"
      application_types: [] as any[],
      testing_policies: [] as any[],
      from_acceptance_rate: null as any,
      to_acceptance_rate: null as any,
      from_graduation_rate: null as any,
      to_graduation_rate: null as any,
      from_graduation_rate_4yr: null as any,
      to_graduation_rate_4yr: null as any,
      from_retention_rate: null as any,
      to_retention_rate: null as any,
      from_in_state_tuition: null as any,
      to_in_state_tuition: null as any,
      from_out_of_state_tuition: null as any,
      to_out_of_state_tuition: null as any,
      from_room_board: null as any,
      to_room_board: null as any,
      from_net_price: null as any,
      to_net_price: null as any,
      offers_merit_aid: false,
      meets_full_need: false,
      has_loan_free_policy: false,
      has_free_tuition_policy: false,
      from_median_earnings: null as any,
      to_median_earnings: null as any,
      from_employment_rate: null as any,
      to_employment_rate: null as any,
      to_loan_default_rate: null as any,
      gender_types: [] as any[],
      // SPEC_College_Search_Filters (v1.1) — sort key (server-side whitelist)
      sort: '' as string,
    },
    onSubmit: async (val) => {
      setPage(1);
      searchCollege(val, 1);
      // SPEC_College_Search_Filters (v1.1) — persist per-user (best-effort).
      // Keyword is volatile search text, not a saved filter preference.
      const sid = getUserId();
      if (sid && filtersRestored.current) {
        const { keyword, ...filtersToSave } = val as any;
        void collegeServices.saveSearchFilters(sid, filtersToSave);
      }
    },
  });
  // Guards the initial mount: skip auto-search/auto-save until persisted
  // filters have been restored, so we don't overwrite them with defaults.
  const filtersRestored = React.useRef(false);

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const searchCollege = async (filter: any, pageNumberInput: number) => {
    setLoading(true);
    const [res, total]: any = await collegeServices.getUSCollegeV1({
      life_frat_nat: filter?.life_frat_nat === 'true' ? 'Y' : filter?.life_frat_nat === 'false' ? 'N' : filter?.life_frat_nat,
      life_sor_nat:  filter?.life_sor_nat === 'true'  ? 'Y' : filter?.life_sor_nat === 'false'  ? 'N' : filter?.life_sor_nat,
      take: 100,
      skip: (pageNumberInput - 1) * 100,
      type_of_schools: filter.type_of_school?.length ? filter.type_of_school : undefined,
      regions: filter.region,
      keyword: filter.keyword,
      state_codes: filter.state_codes,
      state_name: filter.state_name,
      cmps_settings: filter.cmps_setting?.length ? filter.cmps_setting : undefined,
      sizes_of_school: filter.size_of_school?.length ? filter.size_of_school : undefined,
      sport_codes: filter.sport_codes,
      major_names: filter.major_names,
      from_gpa: filter.from_gpa,
      to_gpa: filter.to_gpa,
      from_sat: filter.from_sat,
      to_sat: filter.to_sat,
      from_act: filter.from_act,
      to_act: filter.to_act,
      year_types: filter.year_types?.length ? filter.year_types : undefined,
      // SPEC_College_Search_Filters (v1.1) — Tier 1 + Tier 2
      application_types: filter.application_types?.length ? filter.application_types : undefined,
      testing_policies: filter.testing_policies?.length ? filter.testing_policies : undefined,
      from_acceptance_rate: filter.from_acceptance_rate,
      to_acceptance_rate: filter.to_acceptance_rate,
      from_graduation_rate: filter.from_graduation_rate,
      to_graduation_rate: filter.to_graduation_rate,
      from_graduation_rate_4yr: filter.from_graduation_rate_4yr,
      to_graduation_rate_4yr: filter.to_graduation_rate_4yr,
      from_retention_rate: filter.from_retention_rate,
      to_retention_rate: filter.to_retention_rate,
      from_in_state_tuition: filter.from_in_state_tuition,
      to_in_state_tuition: filter.to_in_state_tuition,
      from_out_of_state_tuition: filter.from_out_of_state_tuition,
      to_out_of_state_tuition: filter.to_out_of_state_tuition,
      from_room_board: filter.from_room_board,
      to_room_board: filter.to_room_board,
      from_net_price: filter.from_net_price,
      to_net_price: filter.to_net_price,
      offers_merit_aid: filter.offers_merit_aid || undefined,
      meets_full_need: filter.meets_full_need || undefined,
      has_loan_free_policy: filter.has_loan_free_policy || undefined,
      has_free_tuition_policy: filter.has_free_tuition_policy || undefined,
      from_median_earnings: filter.from_median_earnings,
      to_median_earnings: filter.to_median_earnings,
      from_employment_rate: filter.from_employment_rate,
      to_employment_rate: filter.to_employment_rate,
      to_loan_default_rate: filter.to_loan_default_rate,
      gender_types: filter.gender_types?.length ? filter.gender_types : undefined,
      sort: filter.sort || undefined,
    });
    setColleges(res);
    setTotalResults(typeof total === 'number' ? total : 0);
    setTotalPage(Math.ceil(total / 100));
    setLoading(false);
  };

  // SPEC_College_Search_Filters (v1.1) — live search with 250ms debounce.
  // Stored in a ref so the debounced timer survives re-renders; searchCollege
  // reads its filter argument (no stale-closure on formik state).
  const debouncedSearch = React.useRef(
    debounce((filter: any) => {
      setPage(1);
      searchCollege(filter, 1);
    }, 250),
  ).current;
  React.useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const loadLists = React.useCallback(async () => {
    studentId = getUserId();
    token = getAccessToken();
    if (!token || !studentId) {
      handleLogout();
      return;
    }
    setLoadingLists(true);
    try {
      const [res, listsRes] = await Promise.all([
        collegeServices.getMyCollege(studentId),
        collegeServices.getCollegeLists(studentId),
      ]);
      const parsed = res?.colleges_array ? checkEnableParseJSON(res.colleges_array, []) : [];
      const groups = Array.isArray(parsed) ? parsed : [];
      // is_saved may live on counselor-named groups; fold into My Colleges for the UI.
      const savedColleges: TCollege[] = [];
      const seen = new Set<string>();
      groups.forEach((g: CollegeListGroup) => {
        (g.collegeList ?? []).forEach((c) => {
          if (!c?.is_saved) return;
          const key = getCollegeKey(c);
          if (key && seen.has(key)) return;
          if (key) seen.add(key);
          savedColleges.push(c);
        });
      });
      const myGroup: CollegeListGroup = {
        name: MY_COLLEGES_LIST_NAME,
        collegeList: savedColleges,
      };
      setMyCurrentList([myGroup]);
      setData(res);
      setSavedStudentLists(
        (listsRes?.studentLists ?? []).map(mapApiCollegeListGroup),
      );
    } finally {
      setLoadingLists(false);
    }
  }, []);

  React.useEffect(() => {
    void loadLists();
  }, [loadLists]);

  React.useEffect(() => {
    // Skip the initial mount run; the filter-restore effect performs the first
    // search after applying any persisted filters.
    if (!filtersRestored.current) return;
    searchCollege(formik.values, page);
  }, [page]);

  // SPEC_College_Search_Filters (v1.1) — restore persisted per-user filters on
  // mount (single source of truth shared with CAM Select Colleges).
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const sid = getUserId();
      let merged = formik.values;
      if (sid) {
        const saved = await collegeServices.getSearchFilters(sid);
        if (!cancelled && saved && Object.keys(saved).length > 0) {
          const {
            cip_ids: _legacyCipIds,
            religious_affiliations: _legacyReligious,
            carnegie_classifications: _legacyCarnegie,
            hbcu: _legacyHbcu,
            hispanic_serving_institution: _legacyHsi,
            from_campus_size_acres: _legacyFromAcres,
            to_campus_size_acres: _legacyToAcres,
            ...rest
          } = saved;
          merged = {
            ...formik.values,
            ...rest,
            major_names: saved.major_names ?? [],
          };
          formik.setValues(merged);
        }
      }
      if (cancelled) return;
      filtersRestored.current = true;
      searchCollege(merged, 1);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistMyColleges = async (myGroup: CollegeListGroup, dataInput: any) => {
    studentId = getUserId(); token = getAccessToken();
    if (token && studentId) {
      setSaving(true);
      try {
        const payload = JSON.stringify([myGroup]);
        // getMyCollege no longer returns a row `id` after picks unification.
        const existingGroups = dataInput?.colleges_array
          ? checkEnableParseJSON(dataInput.colleges_array, [])
          : [];
        const hasExistingPicks =
          !!dataInput?.id ||
          (Array.isArray(existingGroups) &&
            existingGroups.some(
              (g: CollegeListGroup) =>
                Array.isArray(g?.collegeList) && g.collegeList.length > 0
            ));
        if (hasExistingPicks) {
          await collegeServices.updatetMyCollege(studentId, payload);
        } else {
          await collegeServices.createMyCollege(studentId, payload);
        }
        const fresh: MyCollegesRecord = await collegeServices.getMyCollege(studentId);
        const parsed = fresh.colleges_array ? checkEnableParseJSON(fresh.colleges_array, []) : [];
        const groups = Array.isArray(parsed) ? parsed : [];
        const savedColleges: TCollege[] = [];
        const seen = new Set<string>();
        groups.forEach((g: CollegeListGroup) => {
          (g.collegeList ?? []).forEach((c) => {
            if (!c?.is_saved) return;
            const key = getCollegeKey(c);
            if (key && seen.has(key)) return;
            if (key) seen.add(key);
            savedColleges.push(c);
          });
        });
        setMyCurrentList([
          {
            name: MY_COLLEGES_LIST_NAME,
            collegeList: savedColleges.length > 0 ? savedColleges : myGroup.collegeList,
          },
        ]);
        setData(fresh);
      } finally { setSaving(false); }
    } else { handleLogout(); }
  };

  const myCollegesGroup = myCurrentList[0] ?? {
    name: MY_COLLEGES_LIST_NAME,
    collegeList: [],
  };

  const handleClickAddToList = async (item: TCollege, currentListItem: CollegeListGroup) => {
    if (saving) return;
    const isMyColleges = currentListItem.name === MY_COLLEGES_LIST_NAME;
    if (isMyColleges) {
      const alreadySaved = myCollegesGroup.collegeList.some(
        (c) => c.is_saved && isSameCollege(c, item),
      );
      if (alreadySaved) return;
      const savedCount = myCollegesGroup.collegeList.filter((c) => c.is_saved).length;
      if (savedCount >= mySchoolsMax) {
        toast.error(
          `You've reached the Starter limit of ${mySchoolsMax} schools in My Colleges. Upgrade for unlimited.`,
        );
        return;
      }
      const college = { ...item, is_saved: true };
      const updated: CollegeListGroup = {
        ...myCollegesGroup,
        collegeList: [...myCollegesGroup.collegeList, college].filter(
          (entry, i, arr) => arr.findLastIndex((c: TCollege) => isSameCollege(c, entry)) === i,
        ),
      };
      setMyCurrentList([updated]);
      if (!loadingLists && data !== null) void persistMyColleges(updated, data);
      return;
    }
    if (!currentListItem.id) return;
    studentId = getUserId();
    if (!studentId) return;
    setSaving(true);
    try {
      await collegeServices.addCollegeListItem(currentListItem.id, studentId, {
        ...item,
        is_saved: false,
      });
      const listsRes = await collegeServices.getCollegeLists(studentId);
      setSavedStudentLists(
        (listsRes?.studentLists ?? []).map(mapApiCollegeListGroup),
      );
      toast.success(`Saved to "${currentListItem.name}"`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewList = async (name: string, item: TCollege | undefined | null) => {
    if (saving) return;
    const trimmed = name?.trim();
    if (!trimmed) {
      setOpenModal(null);
      return;
    }
    const existing = savedStudentLists.find((l) => l.name === trimmed);
    if (existing && item) {
      await handleClickAddToList(item, existing);
      setOpenModal(null);
      return;
    }
    studentId = getUserId();
    if (!studentId) return;
    setSaving(true);
    try {
      const created: any = await collegeServices.createCollegeList(studentId, trimmed);
      if (item && created?.id) {
        await collegeServices.addCollegeListItem(created.id, studentId, {
          ...item,
          is_saved: false,
        });
      }
      const listsRes = await collegeServices.getCollegeLists(studentId);
      setSavedStudentLists(
        (listsRes?.studentLists ?? []).map(mapApiCollegeListGroup),
      );
      toast.success(
        item ? `Created list "${trimmed}"` : `Created list "${trimmed}"`,
      );
    } catch {
      // http interceptor shows API error
    } finally {
      setSaving(false);
      setOpenModal(null);
    }
  };

  const handleRemoveSchool = (item: TCollege, listName: string) => {
    const newList = myCurrentList.map((list) =>
      list.name === listName
        ? { name: list.name, collegeList: list.collegeList.filter((c) => !isSameCollege(c, item)) }
        : list
    );
    setMyCurrentList(newList);
    const myGroup = newList.find((g) => g.name === MY_COLLEGES_LIST_NAME);
    if (myGroup && listName === MY_COLLEGES_LIST_NAME && !loadingLists && data !== null) {
      void persistMyColleges(myGroup, data);
    }
  };

  const handleRemoveList = (index: number) => {
    const newList = myCurrentList.filter((_, i) => i !== index);
    setMyCurrentList(newList);
    const myGroup = newList.find((g) => g.name === MY_COLLEGES_LIST_NAME);
    if (myGroup && !loadingLists && data !== null) void persistMyColleges(myGroup, data);
  };

  const handleCheckedItem = (item: TCollege, index: number, listName: string) => {
    if (listName === MY_COLLEGES_LIST_NAME && item.is_saved) {
      const prev = myCollegesGroup.collegeList[index];
      const wasSaved = !!prev?.is_saved;
      const savedCount = myCollegesGroup.collegeList.filter((c) => c.is_saved).length;
      if (!wasSaved && savedCount >= mySchoolsMax) {
        toast.error(
          `You've reached the Starter limit of ${mySchoolsMax} schools in My Colleges. Upgrade for unlimited.`,
        );
        return;
      }
    }
    const newList = myCurrentList.map((list) => {
      if (list.name !== listName) return list;
      const newCollegeList = [...list.collegeList];
      newCollegeList[index] = item;
      return { name: list.name, collegeList: newCollegeList };
    });
    setMyCurrentList(newList);
    const myGroup = newList.find((g) => g.name === MY_COLLEGES_LIST_NAME);
    if (myGroup && listName === MY_COLLEGES_LIST_NAME && !loadingLists && data !== null) {
      void persistMyColleges(myGroup, data);
    }
  };

  const [listCounselorConnection, setListCounselorConnection] = React.useState<any[]>([]);
  React.useEffect(() => {
    counselorServices.getCounselors(studentId).then((res: any[]) => {
      setListCounselorConnection(res ?? []);
    });
  }, [studentId]);

  // ── Filter counts (Lovable 7-group taxonomy) ──────────────────────────────
  const v = formik.values;
  const rng = (a: string, b: string) =>
    (v as any)[a] != null || (v as any)[b] != null ? 1 : 0;
  const countLocation = v.state_codes.length;
  const countMajors = v.major_names.length;
  const countTypeSize =
    ((v.type_of_school ?? []).includes('G') ? 1 : 0) +
    ((v.type_of_school ?? []).includes('P') ? 1 : 0) +
    (v.size_of_school?.length ?? 0) +
    (v.cmps_setting?.length ?? 0) +
    (v.gender_types?.length ?? 0);
  const AdmissionCount =
    rng('from_acceptance_rate', 'to_acceptance_rate') +
    rng('from_gpa', 'to_gpa') +
    rng('from_sat', 'to_sat') +
    rng('from_act', 'to_act') +
    (v.testing_policies?.length ?? 0) +
    (v.application_types?.length ?? 0);
  const countCost =
    rng('from_in_state_tuition', 'to_in_state_tuition') +
    rng('from_out_of_state_tuition', 'to_out_of_state_tuition') +
    rng('from_room_board', 'to_room_board') +
    rng('from_net_price', 'to_net_price') +
    (v.offers_merit_aid ? 1 : 0) +
    (v.meets_full_need ? 1 : 0) +
    (v.has_loan_free_policy ? 1 : 0) +
    (v.has_free_tuition_policy ? 1 : 0);
  const countOutcomes =
    rng('from_median_earnings', 'to_median_earnings') +
    rng('from_employment_rate', 'to_employment_rate') +
    rng('from_graduation_rate_4yr', 'to_graduation_rate_4yr') +
    rng('from_graduation_rate', 'to_graduation_rate') +
    rng('from_retention_rate', 'to_retention_rate') +
    (v.to_loan_default_rate != null ? 1 : 0);

  const resetGroup = (patch: Record<string, unknown>) => {
    formik.setValues({ ...formik.values, ...patch });
    formik.handleSubmit();
  };

  // Active chips for the dismissible chips row
  const activeChips: { label: string; onRemove: () => void }[] = [
    ...(countLocation > 0 ? [{ label: `Location (${countLocation})`, onRemove: () => resetGroup({ state_codes: [] }) }] : []),
    ...(countMajors > 0 ? [{ label: `Majors (${countMajors})`, onRemove: () => resetGroup({ major_names: [] }) }] : []),
    ...(countTypeSize > 0 ? [{ label: `Type & Size (${countTypeSize})`, onRemove: () => resetGroup({ type_of_school: [], size_of_school: [], cmps_setting: [], gender_types: [] }) }] : []),
    ...(AdmissionCount > 0 ? [{ label: `Admission (${AdmissionCount})`, onRemove: () => resetGroup({ from_acceptance_rate: null, to_acceptance_rate: null, from_gpa: null, to_gpa: null, from_sat: null, to_sat: null, from_act: null, to_act: null, testing_policies: [], application_types: [] }) }] : []),
    ...(countCost > 0 ? [{ label: `Cost (${countCost})`, onRemove: () => resetGroup({ from_in_state_tuition: null, to_in_state_tuition: null, from_out_of_state_tuition: null, to_out_of_state_tuition: null, from_room_board: null, to_room_board: null, from_net_price: null, to_net_price: null, offers_merit_aid: false, meets_full_need: false, has_loan_free_policy: false, has_free_tuition_policy: false }) }] : []),
    ...(countOutcomes > 0 ? [{ label: `Outcomes (${countOutcomes})`, onRemove: () => resetGroup({ from_median_earnings: null, to_median_earnings: null, from_employment_rate: null, to_employment_rate: null, from_graduation_rate_4yr: null, to_graduation_rate_4yr: null, from_graduation_rate: null, to_graduation_rate: null, from_retention_rate: null, to_retention_rate: null, to_loan_default_rate: null }) }] : []),
  ];

  const clearAll = () => {
    formik.resetForm();
    setKeyword('');
    formik.handleSubmit();
  };

  const FILTERS = [
    { id: 'location',  label: 'Location',    Icon: IconLocation,  count: countLocation,  onClick: () => setOpenLocationSearch(true) },
    { id: 'majors',    label: 'Majors',      Icon: IconMajors,    count: countMajors,    onClick: () => setOpenMajorSearch(true) },
    { id: 'typesize',  label: 'Type & Size', Icon: IconTypeSize,  count: countTypeSize,  onClick: () => setOpenTypeSize(true) },
    { id: 'admission', label: 'Admission',   Icon: IconAdmission, count: AdmissionCount, onClick: () => setOpenAdmissionRequirement(true) },
    { id: 'cost',      label: 'Cost',        Icon: IconCost,      count: countCost,      onClick: () => setOpenCost(true) },
    { id: 'outcomes',  label: 'Outcomes',    Icon: IconOutcomes,  count: countOutcomes,  onClick: () => setOpenOutcomes(true) },
  ];
  const totalActiveFilters = FILTERS.reduce((sum, f) => sum + (f.count || 0), 0);

  // "Hide Reviewed" hides schools already saved in My Colleges (current page).
  const reviewedKeys = new Set(
    myCollegesGroup.collegeList
      .filter((c) => c.is_saved)
      .map((c) => getCollegeKey(c))
      .filter((key): key is string => !!key),
  );
  const displayedColleges = hideReviewed
    ? colleges.filter((c) => {
        const key = getCollegeKey(c);
        return !key || !reviewedKeys.has(key);
      })
    : colleges;

  return (
    <>
      <Box sx={{ maxWidth: 1152, mx: 'auto', px: isMobile ? 2 : 4, py: isMobile ? 2 : 4 }}>

        {/* ── Upgrade banner (Starter) ─────────────────────────────────── */}
        {isStarter && (
          <a
            href="/settings?active-tab=5"
            className="gm-free-banner"
            style={{ marginBottom: 20, display: 'flex' }}
          >
            <span>💡 <strong>Starter plan:</strong> Upgrade to Silver or Gold to unlock unlimited filters, more lists, and counselor messaging.</span>
            <span className="gm-free-banner-arrow">→</span>
          </a>
        )}

        {/* ── Page header ──────────────────────────────────────────────── */}
        <h2 className="gm-page-title" style={{ fontSize: isMobile ? 22 : 28, marginBottom: 4 }}>
          College Search
        </h2>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 24, fontFamily: "'DM Sans', sans-serif" }}>
          Search by school name, then narrow with location, majors, admission, and more.
        </p>

        {/* ── Search bar ───────────────────────────────────────────────── */}
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5, mb: 3 }}>
            <div className="gm-search-input-wrap" style={{ flex: 1 }}>
              <span className="gm-search-icon"><IconSearch /></span>
              <input
                className="form-control"
                style={{ paddingLeft: 38, paddingRight: keyword ? 36 : 12, fontSize: 14, borderRadius: 8, height: 40, borderColor: '#e5e7eb', background: 'white', fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Search by school name…"
                value={keyword}
                onChange={(e) => {
                  const value = e.target.value;
                  setKeyword(value);
                  formik.setFieldValue('keyword', value);
                  // Name-only search (`keyword`). Location/admission/etc. use filter pills below.
                  formik.setFieldValue('state_name', '');
                  // Live search-as-you-type (250ms debounce per spec).
                  if (filtersRestored.current) {
                    debouncedSearch({ ...formik.values, keyword: value, state_name: '' });
                  }
                }}
              />
              {keyword && (
                <button type="button" className="gm-clear-btn" onClick={() => { setKeyword(''); formik.setFieldValue('keyword', ''); formik.setFieldValue('state_name', ''); }}>
                  <IconX size={14} />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="gm-btn-orange"
              style={{ minWidth: 90, height: 40, borderRadius: 8 }}
              onClick={() => formik.handleSubmit()}
            >
              Search
            </button>
          </Box>

          {/* ── Filter pill row ───────────────────────────────────────── */}
          {isMobile ? (
            <Box sx={{ mb: 2.5 }}>
              <button
                type="button"
                className={`gm-filter-pill${totalActiveFilters > 0 ? ' active' : ''}`}
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setOpenMobileFilters(true)}
              >
                Filters
                {totalActiveFilters > 0 && <span className="pill-count">{totalActiveFilters}</span>}
              </button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
              {FILTERS.map((f) => {
                const { Icon } = f;
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`gm-filter-pill${f.count > 0 ? ' active' : ''}`}
                    onClick={f.onClick}
                  >
                    <span style={{ color: BRAND_NAVY, display: 'inline-flex' }}><Icon /></span>
                    {f.label}
                    {f.count > 0 && <span className="pill-count">{f.count}</span>}
                  </button>
                );
              })}
            </Box>
          )}

          {/* ── Active chips row ──────────────────────────────────────── */}
          {activeChips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5, alignItems: 'center' }}>
              {activeChips.map((chip, i) => (
                <span key={i} className="gm-active-chip">
                  {chip.label}
                  <button type="button" aria-label={`Remove ${chip.label}`} onClick={chip.onRemove}>
                    <IconX size={11} />
                  </button>
                </span>
              ))}
              <button
                type="button"
                style={{ marginLeft: 'auto', fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
                onClick={clearAll}
              >
                Clear all filters
              </button>
            </Box>
          )}
        </form>

        {/* ── Results header ────────────────────────────────────────────── */}
        {!loading && colleges.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <p style={{ fontSize: 13, color: '#6B7280', margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
              Showing <strong style={{ color: '#111827' }}>{displayedColleges.length}</strong>
              {totalResults > 0 ? <> of <strong style={{ color: '#111827' }}>{totalResults.toLocaleString()}</strong></> : null} schools
            </p>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6B7280', margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                <Switch
                  size="small"
                  checked={hideReviewed}
                  onChange={(e) => setHideReviewed(e.target.checked)}
                />
                Hide Reviewed
              </label>
              <label htmlFor="gm-sort" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6B7280', margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
                Sort
              <select
                id="gm-sort"
                value={formik.values.sort}
                onChange={(e) => {
                  formik.setFieldValue('sort', e.target.value);
                  setPage(1);
                  searchCollege({ ...formik.values, sort: e.target.value }, 1);
                }}
                style={{ fontSize: 13, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', padding: '0 8px', fontFamily: "'DM Sans', sans-serif", color: '#111827' }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              </label>
            </Box>
          </Box>
        )}

        {/* ── Results grid ──────────────────────────────────────────────── */}
        {loading ? (
          <LoadingContent />
        ) : displayedColleges.length > 0 ? (
          <>
            <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
              {displayedColleges.map((item, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <CollegeResultCard
                    item={item}
                    myCollegesGroup={myCollegesGroup}
                    savedStudentLists={savedStudentLists}
                    maxLists={maxLists}
                    mySchoolsMax={mySchoolsMax}
                    isStarter={isStarter}
                    saving={saving}
                    onAddToList={handleClickAddToList}
                    onOpenNewList={setOpenModal}
                    isMobile={isMobile}
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
              <Pagination
                count={totalPage}
                color="primary"
                page={page}
                onChange={(_, value) => setPage(value)}
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
              />
            </Box>
          </>
        ) : (
          <div className="gm-empty-state">
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 6 }}>No schools match your search.</p>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>Try adjusting your filters or search term.</p>
            <button className="gm-btn-orange" onClick={clearAll} style={{ borderRadius: 8, fontSize: 14 }}>Reset all filters</button>
          </div>
        )}

      </Box>

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <ModalAddList
        open={!!openModal}
        submitting={saving}
        onCancel={() => !saving && setOpenModal(null)}
        handleClose={(str) => void handleAddNewList(str, openModal ?? null)}
      />
      <ModalMajor open={openMajorSearch} setOpen={setOpenMajorSearch} formik={formik} />
      <ModalLocation open={openLocationSearch} setOpen={setOpenLocationSearch} formik={formik} />
      <ModalTypeSize open={openTypeSize} setOpen={setOpenTypeSize} formik={formik} />
      <ModalAdmissionRequirement open={openAdmissionRequirement} setOpen={setOpenAdmissionRequirement} formik={formik} />
      <ModalCost open={openCost} setOpen={setOpenCost} formik={formik} />
      <ModalOutcomes open={openOutcomes} setOpen={setOpenOutcomes} formik={formik} />

      {/* ── Mobile filter drawer (bottom sheet) ───────────────────────── */}
      <Drawer
        anchor="bottom"
        open={openMobileFilters}
        onClose={() => setOpenMobileFilters(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2, pb: 3 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <strong style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16 }}>Filters</strong>
          <button
            type="button"
            aria-label="Close filters"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}
            onClick={() => setOpenMobileFilters(false)}
          >
            <IconX size={18} />
          </button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {FILTERS.map((f) => {
            const { Icon } = f;
            return (
              <button
                key={f.id}
                type="button"
                className={`gm-filter-pill${f.count > 0 ? ' active' : ''}`}
                style={{ justifyContent: 'space-between', width: '100%' }}
                onClick={() => {
                  setOpenMobileFilters(false);
                  f.onClick();
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: BRAND_NAVY, display: 'inline-flex' }}><Icon /></span>
                  {f.label}
                </span>
                {f.count > 0 && <span className="pill-count">{f.count}</span>}
              </button>
            );
          })}
          {totalActiveFilters > 0 && (
            <button
              type="button"
              style={{ marginTop: 8, fontSize: 13, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
              onClick={() => {
                setOpenMobileFilters(false);
                clearAll();
              }}
            >
              Clear all filters
            </button>
          )}
        </Box>
      </Drawer>
    </>
  );
};

// ── College Result Card ────────────────────────────────────────────────────────
function CollegeResultCard({
  item: c,
  myCollegesGroup,
  savedStudentLists,
  maxLists,
  mySchoolsMax,
  isStarter,
  saving,
  onAddToList,
  onOpenNewList,
  isMobile,
}: {
  item: TCollege;
  myCollegesGroup: CollegeListGroup;
  savedStudentLists: CollegeListGroup[];
  maxLists: number;
  mySchoolsMax: number;
  isStarter: boolean;
  saving?: boolean;
  onAddToList: (item: TCollege, list: CollegeListGroup) => void;
  onOpenNewList: (item: TCollege) => void;
  isMobile: boolean;
}) {
  const initials = getInitials(c.name);
  const typeLabel = type_of_school_convert(c.type_of_school);
  const settingLabel = cmpsSettingConvert(c.cmps_setting);

  const inGroups = savedStudentLists.filter((l) =>
    l.collegeList.some((col) => isSameCollege(col, c)),
  );
  const inListsLabel = (() => {
    if (inGroups.length === 0) return '';
    const names = inGroups.map((g) => g.name);
    if (names.length <= 2) return `In: ${names.join(', ')}`;
    return `In: ${names.slice(0, 2).join(', ')}, +${names.length - 2} more`;
  })();

  const [saveOpen, setSaveOpen] = React.useState(false);
  const isInMyColleges = myCollegesGroup.collegeList.some(
    (col) => col.is_saved && isSameCollege(col, c),
  );
  const savedSchoolsCount = myCollegesGroup.collegeList.filter((col) => col.is_saved).length;
  const atSchoolLimit = !isInMyColleges && savedSchoolsCount >= mySchoolsMax;

  const hostname = (() => {
    try { return c.url_address ? new URL(c.url_address).hostname.replace('www.', '') : null; }
    catch { return null; }
  })();

  // SPEC_College_Search_Filters (v1.1) — Lovable card stats/cost/outcomes
  const acceptRaw = c.acceptance_rate != null ? Number(c.acceptance_rate) : null;
  const acceptNum =
    acceptRaw != null && !Number.isNaN(acceptRaw) && acceptRaw > 0 ? acceptRaw : null;
  const acceptBadge =
    acceptNum == null ? 'unknown' : acceptNum < 20 ? 'hard' : acceptNum <= 50 ? 'medium' : 'easy';
  const testScores = [
    c.sat ? `SAT ${c.sat}` : null,
    c.act ? `ACT ${c.act}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const totalCost = (Number(c.in_state_tuition) || 0) + (Number(c.room_board) || 0);

  return (
    <div className="gm-college-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ROW 1 — Identity */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <div className="gm-college-avatar">{initials}</div>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <h4 className="gm-display" style={{ fontWeight: 600, fontSize: 15, color: '#111827', margin: 0, lineHeight: 1.3, wordBreak: 'break-word' }}>
            {c.name}
          </h4>
          <p style={{ fontSize: 12, color: '#6B7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
            <IconMapPin /> {[c.city, c.state].filter(Boolean).join(', ')}
          </p>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {c.year_type && <span className="gm-type-chip">{c.year_type}</span>}
            {typeLabel && <span className="gm-type-chip">{typeLabel}</span>}
            {c.size_of_school && <span className="gm-type-chip">{c.size_of_school}</span>}
            {settingLabel && <span className="gm-type-chip">{settingLabel}</span>}
          </Box>
        </Box>
      </Box>

      {/* ROW 2 — Stats (Acceptance / GPA / Test policy / Test scores) */}
      <Box
        sx={{
          mt: 2,
          pl: '52px',
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' },
          gap: 1.5,
        }}
      >
        <div className="gm-stat">
          <span className="gm-stat-label">Acceptance</span>
          {acceptNum != null ? (
            <span className={`gm-accept-badge ${acceptBadge}`} style={{ alignSelf: 'flex-start' }}>
              {acceptNum}%
            </span>
          ) : (
            <span className="gm-stat-value" style={{ color: '#6B7280' }}>—</span>
          )}
        </div>
        <div className="gm-stat">
          <span className="gm-stat-label">GPA</span>
          <span className="gm-stat-value" style={c.gpa == null ? { color: '#6B7280' } : undefined}>
            {c.gpa ?? '—'}
          </span>
        </div>
        <div className="gm-stat">
          <span className="gm-stat-label">Test policy</span>
          <span className="gm-stat-value" style={!c.testing_policy ? { color: '#6B7280' } : undefined}>
            {c.testing_policy || '—'}
          </span>
        </div>
        <div className="gm-stat">
          <span className="gm-stat-label">Test scores</span>
          <span className="gm-stat-value" style={!testScores ? { color: '#6B7280' } : undefined}>
            {testScores || '—'}
          </span>
        </div>
      </Box>

      {/* ROW 3 — Cost row (total cost + Net Price + Admissions Page) */}
      {(totalCost > 0 || c.url_price_calc || hostname) && (
        <Box sx={{ mt: 2, pl: '52px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 20px', fontSize: 12, color: '#6B7280', fontFamily: "'DM Sans', sans-serif" }}>
          {totalCost > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <IconDollar /> <span style={{ color: '#111827', fontWeight: 500 }}>${totalCost.toLocaleString()}</span> /yr (tuition + room)
            </span>
          )}
          {c.url_price_calc && (
            <a href={c.url_price_calc} target="_blank" rel="noopener noreferrer"
              style={{ color: BRAND_NAVY, textDecoration: 'underline' }}>
              Net Price Calculator
            </a>
          )}
          {hostname && (
            <a href={c.url_address} target="_blank" rel="noopener noreferrer"
              style={{ color: BRAND_NAVY, textDecoration: 'underline' }}>
              Admissions Page
            </a>
          )}
        </Box>
      )}

      {/* ROW 4 — Outcomes line (median earnings) */}
      {c.median_earnings_10yr ? (
        <Box sx={{ mt: 1, pl: '52px', fontSize: 11, color: '#6B7280', fontFamily: "'DM Sans', sans-serif" }}>
          Median earnings 10yr: <span style={{ color: '#111827', fontWeight: 500 }}>${Number(c.median_earnings_10yr).toLocaleString()}</span>
        </Box>
      ) : null}

      {/* "In: ..." label */}
      {inListsLabel && (
        <p className="gm-in-list-label" style={{ marginTop: 8, paddingLeft: 52 }}>{inListsLabel}</p>
      )}

      {/* ROW 4 — Actions */}
      <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'stretch', gap: 1 }}>
        {isInMyColleges ? (
          <span className="gm-in-my-colleges" style={{ flex: '1 0 75%' }}>
            <IconCheck /> In My Colleges
          </span>
        ) : atSchoolLimit ? (
          <button
            className="gm-btn-orange"
            style={{ flex: '1 0 75%', borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: 0.6, cursor: 'not-allowed' }}
            title={`Starter is limited to ${mySchoolsMax} schools — upgrade for unlimited`}
            onClick={() =>
              toast.error(
                `You've reached the Starter limit of ${mySchoolsMax} schools in My Colleges. Upgrade for unlimited.`,
              )
            }
          >
            🔒 {mySchoolsMax} school limit
          </button>
        ) : (
          <button
            className="gm-btn-orange"
            style={{ flex: '1 0 75%', borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            disabled={saving}
            onClick={() => onAddToList(c, myCollegesGroup)}
          >
            {saving ? (
              <>
                <InlineSpinner tone="light" /> Saving...
              </>
            ) : (
              'Add to My Colleges'
            )}
          </button>
        )}

        {/* Bookmark / Save-to-list */}
        <div style={{ position: 'relative', flex: '0 0 auto' }}>
          <button
            className="gm-btn-navy-outline"
            style={{ height: '100%', padding: '0 12px' }}
            disabled={saving}
            onClick={() => !saving && setSaveOpen((s) => !s)}
            aria-label="Save to a list"
          >
            <IconBookmark filled={inGroups.length > 0} />
          </button>
          {saveOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setSaveOpen(false)} />
              <div className="gm-save-popover" style={{ position: 'absolute', bottom: '110%', right: 0, zIndex: 50 }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', padding: '4px 8px 6px', margin: 0 }}>Save to list</p>
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {savedStudentLists.length === 0 && (
                    <p style={{ fontSize: 12, color: '#6B7280', padding: '4px 8px', margin: 0 }}>No lists yet — create one below.</p>
                  )}
                  {savedStudentLists.map((g) => {
                    const checked = g.collegeList.some((col) => isSameCollege(col, c));
                    return (
                      <label
                        key={g.id ?? g.name}
                        className="gm-save-popover-item"
                        style={{ cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={saving}
                          onChange={() => { if (!saving) { onAddToList(c, g); setSaveOpen(false); } }}
                          style={{ accentColor: BRAND_NAVY, width: 14, height: 14 }}
                        />
                        <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                      </label>
                    );
                  })}
                </div>
                {savedStudentLists.length < maxLists && (
                  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 4 }}>
                    <button
                      className="gm-save-popover-item"
                      style={{
                        width: '100%',
                        color: BRAND_NAVY,
                        fontSize: 12,
                        fontWeight: 600,
                        background: 'none',
                        border: 'none',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.6 : 1,
                      }}
                      disabled={saving}
                      onClick={() => { if (!saving) { setSaveOpen(false); onOpenNewList(c); } }}
                    >
                      + Create new list
                    </button>
                  </div>
                )}
                {savedStudentLists.length >= maxLists && (
                  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 6, paddingLeft: 8, paddingBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>🔒 {isStarter ? 'Upgrade to add more lists' : 'Max lists reached'}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Box>
    </div>
  );
}

// ── ModalAddList ──────────────────────────────────────────────────────────────
export const ModalAddList = ({
  open,
  handleClose,
  onCancel,
  submitting = false,
  initialName = '',
  title = 'List Name',
  submitLabel = 'Create List',
  submittingLabel = 'Creating...',
}: {
  open: boolean;
  handleClose: (name: string) => void | Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
  /** Prefill for rename; leave empty when creating. */
  initialName?: string;
  title?: string;
  submitLabel?: string;
  submittingLabel?: string;
}) => {
  const [listName, setListName] = React.useState('');
  const [validated, setValidated] = React.useState(true);

  React.useEffect(() => {
    if (open) {
      setListName(initialName);
      setValidated(true);
    }
  }, [open, initialName]);

  const handleSave = () => {
    if (submitting) return;
    const value = listName.trim();
    if (!value) {
      setValidated(false);
      return;
    }
    void Promise.resolve(handleClose(value));
  };

  return (
    <Modal
      show={open}
      onHide={() => !submitting && onCancel?.()}
      centered
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18 }}>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          validated={validated}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Form.Group className="mb-3" controlId="collegeListName">
            <Form.Label>List Name</Form.Label>
            <Form.Control
              required
              autoFocus
              value={listName}
              disabled={submitting}
              onChange={(e) => setListName(e.target.value)}
              isInvalid={!validated}
            />
            <Form.Control.Feedback type="invalid">List name is required.</Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="gm-btn-navy-outline"
          style={{ borderRadius: 8, fontSize: 14, marginRight: 8 }}
          disabled={submitting}
          onClick={() => onCancel?.()}
        >
          Cancel
        </button>
        <button
          type="button"
          className="gm-btn-orange"
          style={{ borderRadius: 8, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          disabled={submitting}
          onClick={handleSave}
        >
          {submitting ? (
            <>
              <InlineSpinner tone="light" /> {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// ── buildLineRow (exported, used elsewhere) ───────────────────────────────────
export const buildLineRow = (content: (string | ReactNode)[]) => (
  <Stack className="w-100 py-2" direction="row" justifyContent="space-between" alignItems="center">
    <span>{content?.[0]}</span>
    <span>{content?.[1]}</span>
  </Stack>
);

// ── CollegeInformationDetail (exported, used by MyCollege) ───────────────────
export const CollegeInformationDetail = ({
  miniItem,
  handleRemoveSchool,
  handleUnSave,
  index,
  item,
  handleCheckedItem,
  saveDisabled = false,
}: {
  miniItem: TCollege;
  handleRemoveSchool?: any;
  handleUnSave?: any;
  index: number;
  item: any;
  handleCheckedItem?: any;
  saveDisabled?: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const initials = getInitials(miniItem.name);
  const typeLabel = type_of_school_convert(miniItem?.type_of_school);
  const cmpsLabel = cmpsSettingConvert(miniItem?.cmps_setting);

  const hostname = (() => {
    try { return miniItem.url_address ? new URL(miniItem.url_address).hostname.replace('www.', '') : null; }
    catch { return null; }
  })();

  return (
    <div className="gm-college-card" style={{ marginBottom: 12 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <div className="gm-college-avatar" style={{ flexShrink: 0 }}>{initials}</div>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
            <Box>
              <h4 className="gm-display" style={{ fontWeight: 600, fontSize: 15, color: '#111827', margin: 0, lineHeight: 1.3 }}>
                {miniItem.name}
              </h4>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
                <IconMapPin /> {[miniItem.city, miniItem.state].filter(Boolean).join(', ')}
              </p>
            </Box>
            {handleRemoveSchool && (
              <Button variant="outline-danger" size="sm" onClick={() => handleRemoveSchool(miniItem, item.name)}>Remove</Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {miniItem.year_type && <span className="gm-type-chip">{miniItem.year_type}</span>}
            {typeLabel && <span className="gm-type-chip">{typeLabel}</span>}
            {miniItem.size_of_school && <span className="gm-type-chip">{miniItem.size_of_school}</span>}
            {cmpsLabel && <span className="gm-type-chip">{cmpsLabel}</span>}
          </Box>
        </Box>
      </Box>

      {/* Info rows */}
      {[
        ['Address', miniItem.address_line_1 || 'N/A'],
        ['Website', hostname ? (
          <a href={miniItem.url_address} target="_blank" rel="noreferrer"
            style={{ color: BRAND_NAVY, textDecoration: 'underline', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
            <IconLink /> {hostname}
          </a>
        ) : 'N/A'],
        ['Net Price Calc', miniItem.url_price_calc ? (
          <a href={miniItem.url_price_calc} target="_blank" rel="noreferrer"
            style={{ color: BRAND_NAVY, textDecoration: 'underline', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }}>
            <IconLink /> View
          </a>
        ) : 'N/A'],
        ['GPA', miniItem?.gpa ? `${miniItem.gpa} (Weighted)` : 'N/A'],
        ['SAT', miniItem?.sat ?? 'N/A'],
        ['ACT', miniItem?.act ?? 'N/A'],
        ['Counselor Ranking', miniItem.counselor_ranking ?? 'N/A'],
      ].map(([label, value], i) => (
        <React.Fragment key={i}>
          {i > 0 && <Divider sx={{ my: 1 }} />}
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={5}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontFamily: "'DM Sans', sans-serif" }}>
                {label as string}
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant="body2" sx={{ fontFamily: "'DM Sans', sans-serif" }}>
                {value as ReactNode}
              </Typography>
            </Grid>
          </Grid>
        </React.Fragment>
      ))}

      {handleCheckedItem && (
        <>
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!miniItem.is_saved}
                disabled={saveDisabled}
                onChange={(e) => handleCheckedItem({ ...miniItem, is_saved: e.target.checked }, index, item.name)}
                size="small"
                sx={{ color: BRAND_NAVY, '&.Mui-checked': { color: BRAND_NAVY } }}
              />
            }
            label={<Typography variant="body2" sx={{ fontFamily: "'DM Sans', sans-serif" }}>Save to My Colleges</Typography>}
            sx={{ ml: '0.5px', mt: 0.5 }}
          />
        </>
      )}
    </div>
  );
};

export default CollegeSearch;
